// JSON helpers for the feed's optimistic interactions (likes, comments).
// These talk to the Rails story-post endpoints directly (not via Inertia) so
// the feed stays snappy and preserves per-card state.

export interface Topic {
  name: string;
  slug: string;
}

export interface StoryPost {
  id: string;
  title: string | null;
  displayTitle: string;
  excerpt: string;
  body: string | null;
  status: string;
  verified: boolean;
  reviewed: boolean;
  draft: boolean;
  projectName: string | null;
  projectId: string | null;
  createdAt: string | null;
  viewCount: number;
  images: string[];
  video: string | null;
  topics: Topic[];
  likeCount: number;
  likedByMe: boolean;
  commentCount: number;
  detailUrl: string;
  shareEnabled: boolean;
  publicUrl: string;
}

export interface Comment {
  id: string;
  body: string;
  createdAt: string | null;
  userName: string;
  userAvatarUrl: string | null;
  isMine: boolean;
  likeCount: number;
  likedByMe: boolean;
  replies: Comment[];
}

export interface Liker {
  name: string;
  avatarUrl: string | null;
}

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

async function jsonFetch<T>(url: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
      "X-Requested-With": "XMLHttpRequest",
    },
    ...opts,
  });
  if (res.status === 401) {
    window.location.href = "/login?expired=1";
    throw new Error("unauthorized");
  }
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

export async function createPost(form: FormData): Promise<{ short_uuid: string; path: string }> {
  const res = await fetch("/story_posts", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
      "X-Requested-With": "XMLHttpRequest",
    },
    body: form, // multipart — the browser sets the Content-Type/boundary.
  });
  if (res.status === 401) {
    window.location.href = "/login?expired=1";
    throw new Error("unauthorized");
  }
  if (!res.ok) {
    let message = `Request failed: ${res.status}`;
    try {
      const data = await res.json();
      if (data?.errors?.length) message = data.errors.join(", ");
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json();
}

export const likePost = (id: string) =>
  jsonFetch<{ liked: boolean; count: number }>(`/story_posts/${id}/reaction`, { method: "POST" });

export const togglePublic = (id: string) =>
  jsonFetch<{ public_share_enabled: boolean }>(`/story_posts/${id}/toggle_public`, { method: "PATCH" });

export const updatePost = (id: string, data: { title: string; body: string }) =>
  jsonFetch<{ post: StoryPost }>(`/story_posts/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ story_post: { title: data.title, body: data.body } }),
  });

export async function deletePost(id: string): Promise<void> {
  const res = await fetch(`/story_posts/${id}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
      "X-Requested-With": "XMLHttpRequest",
    },
  });
  if (res.status === 401) {
    window.location.href = "/login?expired=1";
    throw new Error("unauthorized");
  }
  // Treat any 2xx as success; the response body isn't needed and may be empty
  // or a (followed) redirect depending on format negotiation.
  if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
}

export const postLikers = (id: string) =>
  jsonFetch<{ users: Liker[] }>(`/story_posts/${id}/likers`);

export const listComments = (id: string) =>
  jsonFetch<{ comments: Comment[] }>(`/story_posts/${id}/comments`);

export const createComment = (id: string, body: string, parentId?: string | null) =>
  jsonFetch<{ comment: Comment; commentCount: number }>(`/story_posts/${id}/comments`, {
    method: "POST",
    body: JSON.stringify({ story_comment: { body, parent_comment_id: parentId ?? null } }),
  });

export const deleteComment = (postId: string, commentId: string) =>
  jsonFetch<{ ok: boolean; commentCount: number }>(`/story_posts/${postId}/comments/${commentId}`, {
    method: "DELETE",
  });

export const likeComment = (id: string) =>
  jsonFetch<{ liked: boolean; count: number }>(`/story_comments/${id}/reaction`, { method: "POST" });

export const commentLikers = (id: string) =>
  jsonFetch<{ users: Liker[] }>(`/story_comments/${id}/likers`);

export const loadMorePosts = (offset: number, queryString: string) => {
  const qs = [queryString, `offset=${offset}`].filter(Boolean).join("&");
  return jsonFetch<{ posts: StoryPost[]; hasMore: boolean }>(`/feed/stories?${qs}`);
};

/** Compact relative time, e.g. "3h ago". */
export function timeAgo(iso: string | null): string {
  if (!iso) return "";
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}
